<script lang="ts" setup>
  import { useId, useTemplateRef, computed, mergeProps } from 'vue';
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    placeholder?: string | undefined;
    floating?: boolean;
    required?: boolean;
    readonly?: boolean;
    type?: string | undefined;
    multiple?: boolean | undefined;
  }

  defineOptions({ inheritAttrs: false });

  const props = withDefaults(defineProps<Props>(), {
    floating: true,
    required: false,
    readonly: false,
    type: 'text',
    multiple: false,
  });

  const context = useFormContext();
  const random = useId();
  const input = useTemplateRef('input');

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value, input.value),
  });

  const id = computed(() => props.id ?? random);
  const readonly = computed(() => Boolean(props.readonly || (context && context.readonly.value)));

  function onFocus() {
    if (props.type !== 'file' && input.value) {
      input.value.select();
    }
  }

  function onChange(event: Event) {
    if (props.type === 'file') {
      const target = event.target as HTMLInputElement;
      model.value = props.multiple ? target.files : target.files?.[0];
    }
  }
</script>

<template>
  <SfControlWrapper v-slot="{ errorId }" v-bind="{ id, name, label, floating, required }">
    <input
      v-if="type !== 'file'"
      ref="input"
      v-model="model"
      :aria-describedby="errorId"
      :placeholder="placeholder || label"
      :type="type"
      class="control"
      v-bind="mergeProps({ id, name, required, readonly, onFocus }, $attrs)"
    />

    <input
      v-else
      ref="input"
      :aria-describedby="errorId"
      :placeholder="placeholder || label"
      :type="type"
      class="control"
      v-bind="mergeProps({ id, name, required, readonly, multiple, onChange }, $attrs)"
    />
  </SfControlWrapper>
</template>

<style lang="scss" scoped>
  .control {
    &::file-selector-button {
      border: none;
      background: var(--secondary-background);
      border-radius: var(--border-radius-200);
      color: var(--secondary-foreground);
      font-size: var(--sf-input-font-size);
      font-weight: var(--font-weight-bold);
      line-height: var(--sf-input-line-height);
      margin: 0 0.75rem 0 0;
      padding: 0 0.5rem;
      transition-property: background-color, color;
      transition-timing-function: ease-in-out;
      transition-duration: 150ms;

      @media (hover: hover) {
        &:hover {
          background: var(--secondary-background-hover);
          color: var(--secondary-foreground-hover);
          cursor: pointer;
        }
      }
    }
  }
</style>
